/**
 * ActiveSpeurttochtCard Component
 * Dashboard card showing active speurtocht session for the user's team
 */

import React, { useEffect, useState } from "react";
import { View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import { useThemeColors } from "@/providers/ThemeProvider";
import { ActiveSessionData } from "../types";

interface ActiveSpeurttochtCardProps {
  /** Active session data */
  session: ActiveSessionData;
  /** Optional custom onPress handler */
  onPress?: () => void;
}

/**
 * Format time remaining
 */
const formatTimeRemaining = (expiresAt: string | null): string | null => {
  if (!expiresAt) return null;

  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  const diff = expiry - now;

  if (diff <= 0) return "Tijd voorbij!";

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * ActiveSpeurttochtCard component
 * Shows active speurtocht session on the dashboard
 */
export const ActiveSpeurttochtCard: React.FC<ActiveSpeurttochtCardProps> = ({
  session,
  onPress,
}) => {
  const router = useRouter();
  const colors = useThemeColors();
  const [timeRemaining, setTimeRemaining] = useState<string | null>(
    formatTimeRemaining(session.expires_at)
  );

  // Update timer every second
  useEffect(() => {
    if (!session.expires_at) return;

    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(session.expires_at));
    }, 1000);

    return () => clearInterval(interval);
  }, [session.expires_at]);

  /**
   * Handle card press
   */
  const handlePress = () => {
    if (onPress) {
      onPress();
    } else {
      router.push(`/(tabs)/games/speurtocht/${session.speurtocht_id}`);
    }
  };

  const progress = session.completed_steps / session.total_steps;
  const isUrgent = session.expires_at && new Date(session.expires_at).getTime() - Date.now() < 5 * 60 * 1000;

  return (
    <TouchableOpacity
      onPress={handlePress}
      activeOpacity={0.8}
      className="rounded-2xl overflow-hidden"
      style={{
        backgroundColor: `${session.speurtocht_color}15`,
        borderWidth: 2,
        borderColor: session.speurtocht_color,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Actieve speurtocht: ${session.speurtocht_name}`}
    >
      {/* Header */}
      <View
        className="px-4 py-3 flex-row items-center justify-between"
        style={{ backgroundColor: `${session.speurtocht_color}25` }}
      >
        <View className="flex-row items-center flex-1">
          <View
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: session.speurtocht_color }}
          >
            <Ionicons
              name={session.speurtocht_icon as any}
              size={20}
              color="#FFFFFF"
            />
          </View>
          <View className="flex-1">
            <Text
              className="text-xs font-medium uppercase"
              style={{ color: session.speurtocht_color }}
            >
              Actieve Speurtocht
            </Text>
            <Text
              className="text-base font-bold"
              style={{ color: colors.text }}
              numberOfLines={1}
            >
              {session.speurtocht_name}
            </Text>
          </View>
        </View>

        {/* Timer */}
        {timeRemaining && (
          <View
            className="px-3 py-1.5 rounded-lg flex-row items-center"
            style={{
              backgroundColor: isUrgent ? `${colors.error}20` : colors.surface,
            }}
          >
            <Ionicons
              name="time"
              size={14}
              color={isUrgent ? colors.error : colors.textSecondary}
            />
            <Text
              className="ml-1.5 font-bold text-sm"
              style={{ color: isUrgent ? colors.error : colors.text }}
            >
              {timeRemaining}
            </Text>
          </View>
        )}
      </View>

      {/* Content */}
      <View className="px-4 py-3">
        {/* Progress */}
        <View className="flex-row items-center justify-between mb-2">
          <Text className="text-sm" style={{ color: colors.textSecondary }}>
            Voortgang
          </Text>
          <Text
            className="font-bold"
            style={{ color: session.speurtocht_color }}
          >
            {session.completed_steps}/{session.total_steps} stappen
          </Text>
        </View>

        {/* Progress Bar */}
        <View
          className="h-3 rounded-full overflow-hidden"
          style={{ backgroundColor: colors.surfaceSecondary }}
        >
          <View
            className="h-3 rounded-full"
            style={{
              backgroundColor: session.speurtocht_color,
              width: `${progress * 100}%`,
            }}
          />
        </View>

        {/* Stats Row */}
        <View className="flex-row items-center justify-between mt-3">
          <View className="flex-row items-center">
            <Ionicons name="star" size={16} color="#F59E0B" />
            <Text className="ml-1 font-bold" style={{ color: "#F59E0B" }}>
              {session.total_points} punten
            </Text>
          </View>

          <View className="flex-row items-center">
            <Text className="text-sm" style={{ color: colors.textSecondary }}>
              Stap {session.current_step_index + 1}
            </Text>
            <Ionicons
              name="chevron-forward"
              size={18}
              color={session.speurtocht_color}
              style={{ marginLeft: 4 }}
            />
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default ActiveSpeurttochtCard;

